import { PieChart } from '@mui/x-charts/PieChart';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

export type ItemStatusDatum = { label: string; value: number; color?: string };

const valueFormatter = (item: { value: number }) => `${item.value}%`;

export default function ItemsGraph({ data = [] as ItemStatusDatum[] }: { data?: ItemStatusDatum[] }) {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const chartData = data.length > 0 ? data : [];
  const hasNonZero = chartData.some((d) => Number(d.value) > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {chartData.length === 0 || !hasNonZero ? (
        <Box sx={{ height: 250, width: 250, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', border: '1px dashed #e0e0e0', borderRadius: 2 }}>
          <Typography variant="body2">{t('dashboard.charts.noData')}</Typography>
        </Box>
      ) : (
        <PieChart
          series={[
            {
              data: chartData,
              highlightScope: { fade: 'global', highlight: 'item' },
              faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
              valueFormatter,
              innerRadius: 60,
              outerRadius: 100,
              paddingAngle: 2,
              cornerRadius: 5,
            },
          ]}
          height={250}
          width={250}
        />
      )}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('dashboard.charts.itemStatus.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {hasNonZero && chartData.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: item.color || '#999',
                  borderRadius: '2px',
                }}
              />
              <Typography variant="body2" sx={{ minWidth: 100 }}>
                {item.label}:
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {item.value}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
